import { useEffect, useState } from "react";
import { Card, Input, Select, Space, Spin } from "antd";
import api from "../interceptors/api";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import StandardButton from "../components/StandardButton";
import ProtectedToolWrapper from "../components/ProtectedToolWrapper";

const { TextArea } = Input;
const { Option } = Select;

const modelsByProvider = {
  Gemini: ["imagen-4.0-generate-001", "imagen-4.0-ultra-generate-001", "imagen-4.0-fast-generate-001", "imagen-3.0-generate-002"],
  OpenAI: ["gpt-image-1", "dall-e-3", "dall-e-2"],
  XAI: ["grok-2-image-1212"],
};

// Prompt presets that still keep it "prompt-only"
const PRESETS = [
  // Developer & Tech Humor
  { label: "Classic Top/Bottom Meme", prompt: "Create a meme image with bold white Impact font and thick black outline. TOP TEXT: WHEN THE BUILD FINALLY PASSES. BOTTOM TEXT: BUT THE BUGS ARE WAITING. Use a dramatic or funny background." },
  { label: "Demotivational Poster", prompt: "Create a demotivational poster: black background, a centered photo with a thin white border, large serif title text, and a smaller sarcastic subtitle. Theme: DEPLOYMENT — 'It works on production, until it doesn't.'" },
  { label: "Tweet-Style Meme", prompt: "Create an image that looks like a funny tweet screenshot with a profile pic, handle, timestamp, and the text: 'me: I'll just fix one bug — also me, 6 hours later: rewriting the app.'" },
  { label: "Sticker Collage Meme", prompt: "Create a meme image as a playful sticker collage with bold central caption: 'SHIP IT'. Include techy doodles and subtle texture." },
  { label: "Minimalist Text Meme", prompt: "Create a clean, minimalist meme with a plain solid background and centered bold text: '404 Motivation Not Found'." },
  { label: "Retro 90s Aesthetic Meme", prompt: "Create a nostalgic 90s-style meme with pixel art, neon colors, and glitch text. Caption: 'Deploying to production in style'." },

  // Internet Classics
  { label: "Two-Button Choice Meme", prompt: "Create an image in the style of the 'Two Buttons' meme: A sweating cartoon character deciding between two red buttons. Labels: 'Fix Bug' and 'Go Home on Time'." },
  { label: "Drake Hotline Bling", prompt: "Create a meme in the 'Drake Hotline Bling' style: First panel Drake rejecting 'Manual Testing', second panel Drake approving 'Automated Testing'." },
  { label: "Distracted Boyfriend", prompt: "Create a meme in the 'Distracted Boyfriend' style: Boyfriend labeled 'Me', girlfriend labeled 'Current Project', other woman labeled 'New Side Project'." },
  { label: "Surprised Pikachu", prompt: "Create a meme in the style of 'Surprised Pikachu': Caption — 'I skipped writing tests... App crashes in production'." },
  { label: "Is This a Pigeon?", prompt: "Create a meme in the 'Is This a Pigeon?' style: Man labeled 'Junior Dev', butterfly labeled 'Any Error Message', caption 'Is this a database issue?'." },
  { label: "Expanding Brain", prompt: "Create a meme in the 'Expanding Brain' style showing four stages: 'Printing debug logs', 'Using a debugger', 'Automated tests', 'AI-assisted coding'." },
  { label: "Change My Mind Table", prompt: "Create a meme in the 'Change My Mind' style: Person sitting at table with sign: 'Tabs are better than spaces. Change my mind.'." },
  { label: "Galaxy Brain / Enlightenment", prompt: "Create a meme showing a human brain progressing to cosmic enlightenment. Theme: Learning git commit, git push, and then git force-push." },
  { label: "UNO Draw 25 Cards", prompt: "Create a meme in the UNO Draw 25 style: Card says 'Write Unit Tests', player drawing 25 cards labeled 'Me'." },
  { label: "Spongebob Mocking Text", prompt: "Create a meme in the Spongebob 'Mocking' style: Caption — 'I dOn'T NeEd CoDe ReViEw'." },
  { label: "Woman Yelling at Cat", prompt: "Create a meme with two panels: left panel — woman yelling labeled 'Project Manager'; right panel — white cat at dinner table labeled 'Developer' with text 'It works on my machine'." },
  { label: "One Does Not Simply", prompt: "Create a meme in the 'One Does Not Simply' style: Caption — 'One does not simply deploy to production without breaking something'." },
  { label: "Arthur's Fist", prompt: "Create a meme showing Arthur's clenched fist. Caption: 'When you find the bug after 4 hours and it's a missing semicolon'." },

  // Pop Culture & Situational
  { label: "Oprah You Get a Car", prompt: "Create a meme in the Oprah 'You get a car!' style: Caption — 'You get a bug! You get a bug! Everyone gets a bug!'." },
  { label: "Shut Up and Take My Money", prompt: "Create a meme in the 'Shut Up and Take My Money' style with a developer holding cash for a new shiny framework." },
  { label: "Gru's Plan", prompt: "Create a meme in the 'Gru's Plan' 4-panel style: Step 1: Write feature, Step 2: Deploy to production, Step 3: Bug report, Step 4: Gru looking confused." },
  { label: "Running Away Balloon", prompt: "Create a meme in the 'Running Away Balloon' style: Person labeled 'Me', balloon labeled 'Writing Documentation'." },
  { label: "Hide the Pain Harold", prompt: "Create a meme in the 'Hide the Pain Harold' style: Caption — 'When you say yes to a tight deadline'." },
  { label: "Success Kid", prompt: "Create a meme in the 'Success Kid' style: Caption — 'Fixed bug without introducing 3 new ones'." },
  { label: "First World Problems", prompt: "Create a meme in the 'First World Problems' style: Caption — 'Wrote perfect code... client changed requirements'." },
  { label: "Philosoraptor", prompt: "Create a meme in the 'Philosoraptor' style: Caption — 'If debugging is removing bugs, is programming adding them?'." }
];


export default function MemeCreator() {
  const { t } = useTranslation();
  const [provider, setProvider] = useState("Gemini");
  const [model, setModel] = useState("imagen-4.0-generate-001");
  const [prompt, setPrompt] = useState(PRESETS[0].prompt);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);

  const runGenerate = async () => {
    const userProfileData = JSON.parse(localStorage.getItem('userProfileData') || 'null');
    if (!userProfileData?.id) {
      toast.error('Please log in to use this feature');
      return;
    }

    if (!prompt.trim()) return toast.error(t("please_enter_prompt"));

    const loadingId = toast.loading(t("generating"));
    setLoading(true);

    try {
      const res = await api.post("/multi-model-image-generator", { provider, model, prompt });

      const urls = res?.data?.images || [];
      if (!urls.length) {
        toast.error(t("no_images_returned"));
        return;
      }
      setImages((prev) => [...urls, ...prev]);
      toast.success(t("done"));
    } catch (e) {
      toast.error(e?.response?.data?.error || t("api_request_failed"));
    } finally {
      toast.dismiss(loadingId);
      setLoading(false);
    }
  };

  const download = (src, idx) => {
    const a = document.createElement("a");
    a.href = src;
    a.download = `meme-${idx + 1}.png`;
    a.click();
  };

  return (
    <ProtectedToolWrapper>
      <div className="max-w-5xl mx-auto px-3 md:px-0">
        <Card className="p-5 md:p-6 rounded-xl shadow-lg">
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            {/* Provider / Model */}
            <Space wrap>
              <div>
                <div className="text-xs text-gray-600 mb-1">{t("provider")}</div>
                <Select
                  value={provider}
                  onChange={(val) => {
                    setProvider(val);
                    setModel(modelsByProvider[val][0]);
                  }}
                  style={{ width: 160 }}
                >
                  {Object.keys(modelsByProvider).map((p) => (
                    <Option key={p} value={p}>
                      {p}
                    </Option>
                  ))}
                </Select>
              </div>
              <div>
                <div className="text-xs text-gray-600 mb-1">{t("model")}</div>
                <Select value={model} onChange={setModel} style={{ width: 220 }}>
                  {modelsByProvider[provider].map((m) => (
                    <Option key={m} value={m}>
                      {m}
                    </Option>
                  ))}
                </Select>
              </div>
            </Space>

            <div>
              <div className="text-xs text-gray-600 mb-1">{t("preset_text")}</div>
              <Select
                value={prompt}
                onChange={(val) => setPrompt(val)}
                style={{ width: 300 }}
              >
                {PRESETS.map((p) => (
                  <Option key={p.label} value={p.prompt}>
                    {p.label}
                  </Option>
                ))}
              </Select>
            </div>

            {/* Prompt box */}
            <div>
              <TextArea
                rows={5}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={t("enter_image_prompt")}
              />
              <Space style={{ marginTop: 8 }}>
                <StandardButton 
                  loading={loading} 
                  onClick={runGenerate} 
                  type="secondary"
                >
                  {t("generate")}
                </StandardButton>
              </Space>
            </div>

            {/* Gallery */}
            {images.length === 0 && !loading && (
              <div className="text-center py-8 text-gray-500">
                {t("no_images_yet")}
              </div>
            )}

            {loading && (
              <div className="text-center py-8">
                <Spin size="large" />
                <div className="mt-2 text-gray-600">{t("generating")}...</div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {images.map((src, idx) => (
                <Card
                  key={`${src}-${idx}`}
                  cover={
                    <img
                      src={src}
                      alt={`Meme ${idx + 1}`}
                      className="w-full h-auto object-cover"
                      style={{ 
                        borderTopLeftRadius: 8, 
                        borderTopRightRadius: 8,
                        height: "250px"
                      }}
                    />
                  }
                  actions={[
                    <StandardButton 
                      size="small" 
                      type="secondary"
                      onClick={() => download(src, idx)}
                    >
                      {t("download")}
                    </StandardButton>,
                  ]}
                  bodyStyle={{ padding: 12 }}
                >
                  <div className="text-xs text-gray-500">Meme #{idx + 1}</div>
                </Card>
              ))}
            </div>
          </Space>
        </Card>
      </div>
    </ProtectedToolWrapper>
  );
}